import { devError } from '@/utils/devLog';
// Page client: catalogue des services.
// - Parcours hierarchique categories -> sous-categories -> produits.
// - Charge et securise les donnees avant affichage.
// - Oriente le client vers les fiches produit pour ajout au panier.
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { Product, Category, SubCategory } from '../../types';
import Card from '../../components/common/Card';
import Header from '../../components/common/Header';
import { ProductCard } from '../../components/common/ProductCard';
import { CategoryCard } from '../../components/common/CategoryCard';

type ViewState = 'categories' | 'subcategories' | 'products';

interface BrowserHeaderProps {
  view: ViewState;
  selectedCategory: Category | null;
  selectedSubCategory: SubCategory | null;
  onGoBack: () => void;
  onResetView: () => void;
  onSelectSubCategory: (sub: SubCategory | null) => void;
}

const BrowserHeader: React.FC<BrowserHeaderProps> = ({
  view,
  selectedCategory,
  selectedSubCategory,
  onGoBack,
  onResetView,
  onSelectSubCategory,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between mb-4">
      <div>
        {view !== 'categories' && (
          <button type="button" onClick={onGoBack} className="text-sm text-primary-600 hover:underline font-tajawal">&larr; {t('ClientServices.navigation.back')}</button>
        )}
      </div>
      <div className="text-sm text-gray-500 dark:text-gray-400 font-tajawal">
        <span className="cursor-pointer hover:underline" onClick={onResetView} role="button" tabIndex={0}>{t('ClientServices.navigation.allCategories')}</span>
        {selectedCategory && (
          <>
            <span className="mx-1">&gt;</span>
            <span
              className={!selectedSubCategory ? 'font-semibold' : 'cursor-pointer hover:underline'}
              onClick={() => onSelectSubCategory(null)}
              onKeyDown={(e) => e.key === 'Enter' && onSelectSubCategory(null)}
              role="button"
              tabIndex={0}
            >
              {t(`categories.${selectedCategory.nom}`, { defaultValue: selectedCategory.nom })}
            </span>
          </>
        )}
        {selectedSubCategory && (
          <>
            <span className="mx-1">&gt;</span>
            <span className="font-semibold">
              {t(`subcategories.${selectedSubCategory.nom}`, { defaultValue: selectedSubCategory.nom })}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

const ClientServices: React.FC = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [view, setView] = useState<ViewState>('categories');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategory | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoadingData(true);
    setError('');
    const fetchData = async () => {
      try {
        const [productsData, categoriesData, subCategoriesData] = await Promise.all([
          api.getProducts(),
          api.getCategories(),
          api.getSubCategories(),
        ]);
        if (cancelled) return;
        // Protections : tableaux sûrs, pas de null/undefined qui ferait planter le rendu
        const safeProducts = Array.isArray(productsData)
          ? productsData.filter((p: unknown): p is Product => Boolean(p && typeof p === 'object' && (p as Product)?.est_validé))
          : [];
        const safeCategories = Array.isArray(categoriesData)
          ? categoriesData.filter((c): c is Category => Boolean(c && typeof c === 'object' && (c as Category)?.nom != null))
          : [];
        const safeSubCategories = Array.isArray(subCategoriesData)
          ? subCategoriesData.filter((s): s is SubCategory => Boolean(s && typeof s === 'object' && (s as SubCategory)?.nom != null))
          : [];
        setProducts(safeProducts);
        setCategories(safeCategories);
        setSubCategories(safeSubCategories);
      } catch (err) {
        if (!cancelled) {
          devError('[ClientServices] Failed to load services page data', err);
          setError(t('ClientServices.state.loadError'));
        }
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  const handleSelectCategory = useCallback((category: Category) => {
    setSelectedCategory(category);
    setView('subcategories');
  }, []);

  const handleSelectSubCategory = useCallback((subCategory: SubCategory) => {
    setSelectedSubCategory(subCategory);
    setView('products');
  }, []);

  const resetView = useCallback(() => {
    setView('categories');
    setSelectedCategory(null);
    setSelectedSubCategory(null);
  }, []);

  const goBack = useCallback(() => {
    if (view === 'products') {
      setSelectedSubCategory(null);
      setView('subcategories');
    } else if (view === 'subcategories') {
      setSelectedCategory(null);
      setView('categories');
    }
  }, [view]);

  const handleSelectSubCategoryNav = useCallback((sub: SubCategory | null) => {
    setSelectedSubCategory(sub);
    setView('subcategories');
  }, []);

  const displayedSubCategories = useMemo(() =>
    subCategories.filter((sc) => sc && selectedCategory && sc.categorie_id === selectedCategory.id),
    [subCategories, selectedCategory]
  );

  const displayedProducts = useMemo(() =>
    products.filter((p) => p && selectedSubCategory && p.sub_category_id === selectedSubCategory.id),
    [products, selectedSubCategory]
  );

  return (
    <div>
      <Header title={t('ClientServices.header.title')} />
      <div className="w-full">
        <Card>
          <BrowserHeader
            view={view}
            selectedCategory={selectedCategory}
            selectedSubCategory={selectedSubCategory}
            onGoBack={goBack}
            onResetView={resetView}
            onSelectSubCategory={handleSelectSubCategoryNav}
          />
          {loadingData && <p className="font-tajawal">{t('ClientServices.state.loading')}</p>}
          {error && <p className="text-red-500 font-tajawal">{error}</p>}

          <div className={view === 'categories' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' : 'hidden'}>
            {categories.map((cat, idx) => {
              if (!cat) return null;
              return (
                <CategoryCard
                  key={cat.id ?? cat.nom ?? `cat-${idx}`}
                  item={cat}
                  onClick={() => handleSelectCategory(cat)}
                />
              );
            })}
          </div>
          <div className={view === 'subcategories' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' : 'hidden'}>
            {displayedSubCategories.map((sub, idx) => {
              if (!sub) return null;
              return (
                <CategoryCard
                  key={sub.id ?? sub.nom ?? `sub-${idx}`}
                  item={sub}
                  onClick={() => handleSelectSubCategory(sub)}
                />
              );
            })}
          </div>
          <div className={view === 'products' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' : 'hidden'}>
            {displayedProducts.map((prod) => {
              if (!prod || prod.id == null || prod.type_vente == null || prod.delivery_method == null) return null;
              return <ProductCard key={prod.id} product={prod} />;
            })}
          </div>

          {view === 'products' && displayedProducts.length === 0 && (
            <div className="text-center py-10 text-gray-500 font-tajawal">
              {t('ClientServices.state.emptyProducts')}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ClientServices;
